import { useEffect } from 'react';
import type { OrderClItem, Stockout, LensProduct } from '../types';
import setLensDataInfo from '../api/setLensDataInfo';

type UseLensOrderInfoParams = {
  facilityId?: number;
  products: LensProduct[];
  productId: number | undefined;
  rightLens: OrderClItem;
  leftLens: OrderClItem;
  isRightLensOrdered: boolean;
  isLeftLensOrdered: boolean;
  setExpectedShipDate: (date: string) => void;
  setShortestArrivalDate: (date: string) => void;
  setArrivalDates: (dates: string[]) => void;
  setRightStockOut: (message: string | undefined) => void;
  setLeftStockOut: (message: string | undefined) => void;
  initialExpectedShipDate: string;
  initialShortestArrivalDate: string;
  initialArrivalDates: string[];
}

const useLensOrderInfo = ({
  facilityId,
  products,
  productId,
  rightLens,
  leftLens,
  isRightLensOrdered,
  isLeftLensOrdered,
  setExpectedShipDate,
  setShortestArrivalDate,
  setArrivalDates,
  setRightStockOut,
  setLeftStockOut,
  initialExpectedShipDate,
  initialShortestArrivalDate,
  initialArrivalDates,
}: UseLensOrderInfoParams): void => {
  const checkStockout = (lens: OrderClItem, stockouts: Stockout[]) => {
    const { fc, tp, cyl, dia } = lens;
    const checkTpRange = (start: string, end: string, tp: string) => {
      var tStart = parseFloat(start);
      var tEnd = parseFloat(end);
      var arrayTp = [];
      for (tEnd; tEnd <= tStart; tEnd+=0.50){arrayTp.push(tEnd)}
      arrayTp = arrayTp.map((num) => num.toFixed(2));
      return arrayTp.includes(tp);
    };
    const stockout = stockouts.find(stockout =>
      stockout.fc === fc &&
      checkTpRange(stockout.tpStart, stockout.tpEnd, tp ?? '') &&
      (stockout.cyl === cyl || stockout.cyl === "") &&
      stockout.dia === dia
    );
    return stockout ? stockout.stockoutMessage : undefined;
  };

  useEffect(() => {
    if (productId) {
      const selectedProduct = products.find(product => product.id === productId);
      if (selectedProduct) {
        const { stockouts } = selectedProduct;

        const setSpecialOrder = async () => {

        const rightOrderData = isRightLensOrdered ? await checkSpecialOrder(rightLens, selectedProduct.id) : undefined;
        const leftOrderData = isLeftLensOrdered ? await checkSpecialOrder(leftLens, selectedProduct.id) : undefined;

        const rightExpectedShippingDate = rightOrderData ? rightOrderData.expectedShipDate : initialExpectedShipDate;
        const rightShortestArrivalDate = rightOrderData ? rightOrderData.shortestArrivalDate : initialShortestArrivalDate;
        const rightArrivalDates = rightOrderData ? rightOrderData.arrivalDates : initialArrivalDates;

        const leftExpectedShippingDate = leftOrderData ? leftOrderData.expectedShipDate : initialExpectedShipDate;
        const leftShortestArrivalDate = leftOrderData ? leftOrderData.shortestArrivalDate : initialShortestArrivalDate;
        const leftArrivalDates = leftOrderData ? leftOrderData.arrivalDates : initialArrivalDates;

          if (new Date(rightExpectedShippingDate) >= new Date(leftExpectedShippingDate)) {
            setExpectedShipDate(rightExpectedShippingDate);
            setShortestArrivalDate(rightShortestArrivalDate);
            setArrivalDates(rightArrivalDates);

          } else if (new Date(leftExpectedShippingDate) > new Date(rightExpectedShippingDate)) {
            setExpectedShipDate(leftExpectedShippingDate);
            setShortestArrivalDate(leftShortestArrivalDate);
            setArrivalDates(leftArrivalDates);

          } else {
            setExpectedShipDate(initialExpectedShipDate);
            setShortestArrivalDate(initialShortestArrivalDate);
            setArrivalDates(initialArrivalDates);

          }
        }
        setSpecialOrder();

        if (stockouts.length > 0) {
          const rightMessage = checkStockout(rightLens, stockouts);
          setRightStockOut(rightMessage);

          const leftMessage = checkStockout(leftLens, stockouts);
          setLeftStockOut(leftMessage);
        }
      }
    }
  }, [rightLens, leftLens, isRightLensOrdered, isLeftLensOrdered, productId]);

  const checkSpecialOrder = async (lens: OrderClItem, productId: number): Promise<{ expectedShipDate: string, shortestArrivalDate: string, arrivalDates: string[] } | undefined> => {
    const { fc, tp, cyl, dia } = lens;
    if( fc !== "" && fc !== undefined && tp !== "" && tp !== undefined && dia !== "" && dia !== undefined) {
      try {
        const response = await setLensDataInfo({
          specialOrder: {
            productId: productId,
            fc: fc,
            tp: tp,
            dia: dia,
            cyl: cyl
          },
          lensDataInfo: {
            facilityId: facilityId,
            expectedShipDate: initialExpectedShipDate
          }
        })

        if (response.data.isSpecialOrder) {
          return {
            expectedShipDate: response.data.expectedShipDate,
            shortestArrivalDate: response.data.shortestArrivalDate,
            arrivalDates: response.data.arrivalDates
          };
        } else {
          return undefined;
        }
      } catch (error) {
        console.error("Error fetching special Order data:", error);
        return undefined;
      }
    }
  };
};

export default useLensOrderInfo;